import {
  Entity,
  parseEntityRef,
  stringifyEntityRef,
} from '@backstage/catalog-model';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  UIEvent,
} from 'react';
import Box from '@mui/material/Box';
import {
  catalogApiRef,
  EntityOwnerFilter,
  EntityUserFilter,
  humanizeEntityRef,
  useEntityList,
  useEntityPresentation,
} from '@backstage/plugin-catalog-react';
import Checkbox from '@mui/material/Checkbox';
import Typography from '@mui/material/Typography';
import Autocomplete, {
  AutocompleteRenderOptionState,
} from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import makeStyles from '@mui/styles/makeStyles';
import { withStyles } from '@mui/styles';
import FormControlLabel from '@mui/material/FormControlLabel';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { useDebouncedEffect } from '@react-hookz/web';
import { humanizeEntity } from './humanize';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  useClearEntityFilters,
  useFetchEntities,
  useOwnedGroups,
} from '@mb.io/core-components';
import Chip from '@mui/material/Chip';
import { isEqual } from 'lodash';
import { useApi } from '@backstage/core-plugin-api';

const useStyles = makeStyles(theme => ({
  common: {
    width: '100%',
    borderRadius: '2px',
    backgroundColor:
      theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.12)'
        : 'rgba(0, 0, 0, 0.08)',
  },
  select: {
    borderBottom:
      theme.palette.mode === 'dark'
        ? '1px solid rgba(255, 255, 255, 0.70)'
        : '1px solid rgba(0, 0, 0, 0.60)',
  },
  selectDisabled: {
    borderBottom: 'none',
  },
  autocomplete: {
    '&:hover': {
      backgroundColor:
        theme.palette.mode === 'dark'
          ? 'rgba(255, 255, 255, 0.16)'
          : 'rgba(0, 0, 0, 0.10)',
    },
  },
}));

const FixedWidthFormControlLabel = withStyles(
  _theme => ({
    label: {
      width: '100%',
    },
    root: {
      width: '90%',
    },
  }),
  { name: 'FixedWidthFormControlLabel' },
)(FormControlLabel);

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

export type EntityOwnerPickerProps = {
  isDisabled?: boolean;
  isMyOwnersOnly?: boolean;
  mode?: 'owners-only' | 'all';
  onSelectedOwnersChange?: (isSelected: boolean) => void;
};

interface RenderOptionLabelProps {
  entity: Entity;
  isSelected: boolean;
}

function RenderOptionLabel(props: Readonly<RenderOptionLabelProps>) {
  const { primaryTitle: title } = useEntityPresentation(props.entity);
  return (
    <Box sx={{ width: '100%' }}>
      <FixedWidthFormControlLabel
        sx={{ width: '100%' }}
        control={
          <Checkbox
            icon={icon}
            checkedIcon={checkedIcon}
            checked={props.isSelected}
          />
        }
        onClick={event => event.preventDefault()}
        label={<Typography noWrap>{title}</Typography>}
      />
    </Box>
  );
}

export const EntityOwnerPicker = (props?: Readonly<EntityOwnerPickerProps>) => {
  const classes = useStyles();
  const {
    isDisabled,
    isMyOwnersOnly,
    mode = 'owners-only',
    onSelectedOwnersChange,
  } = props || {};
  const {
    updateFilters,
    filters,
    queryParameters: { owners: ownersParameter },
  } = useEntityList();
  const catalogApi = useApi(catalogApiRef);

  const prevSelectedOwnersRef = useRef<string[]>();
  const [text, setText] = useState('');
  const { clearFilterState, resetFilters } = useClearEntityFilters();
  const [ownerEntities, setOwnerEntities] = useState<Entity[]>([]);

  const queryParamOwners = useMemo(
    () => [ownersParameter].flat().filter(Boolean) as string[],
    [ownersParameter],
  );

  const matchesPattern = (input: string) => {
    const pattern = /^\w+:\w+\/\w+$/;
    return pattern.test(input);
  };

  const [selectedOwners, setSelectedOwners] = useState(
    queryParamOwners?.length
      ? queryParamOwners.map(param =>
          // Needed so that user can enter catalog using URL from Component page
          matchesPattern(param) ? param : `group:default/${param}`,
        )
      : (filters.owners?.values ?? []),
  );

  useEffect(() => {
    if (!isEqual(prevSelectedOwnersRef.current, selectedOwners)) {
      onSelectedOwnersChange?.(selectedOwners?.length > 0);
      prevSelectedOwnersRef.current = selectedOwners;
    }
  }, [selectedOwners, onSelectedOwnersChange]);

  const [{ value, loading }, handleFetch, cache] = useFetchEntities({
    mode,
    initialSelectedOwnersRefs: selectedOwners,
  });

  useDebouncedEffect(() => handleFetch({ text }), [text, handleFetch], 250);

  const { userGroups, loading: loadingUserGroups } = useOwnedGroups();

  const fetchOwnerEntities = useCallback(
    async (searchText: string) => {
      if (isMyOwnersOnly && userGroups && userGroups?.length > 0) {
        try {
          const entities = await catalogApi.getEntitiesByRefs({
            entityRefs: userGroups,
          });

          const validItems = entities.items.filter(
            (item): item is Entity => item !== undefined,
          );

          const filteredItems = validItems.filter(entity =>
            entity.metadata.name
              ?.toLowerCase()
              .includes(searchText.toLowerCase()),
          );

          if (filteredItems?.length > 0) {
            setOwnerEntities(filteredItems);
          }
        } catch (error) {
          throw new Error('Error fetching owner entities:', error);
        }
      }
    },
    [catalogApi, isMyOwnersOnly, userGroups],
  );

  const handleTextChange = useCallback(
    (newText: string) => {
      if (isMyOwnersOnly) {
        setOwnerEntities([]);
        fetchOwnerEntities(newText);
      }
    },
    [fetchOwnerEntities, isMyOwnersOnly],
  );

  useDebouncedEffect(
    () => {
      handleTextChange(text);
    },
    [text],
    250,
  );

  useEffect(() => {
    if (text) {
      return;
    }

    if (isMyOwnersOnly && selectedOwners?.length === 0) {
      setOwnerEntities([]);
      fetchOwnerEntities('');
    }
  }, [fetchOwnerEntities, isMyOwnersOnly, selectedOwners, text]);

  // Combine selected owners with available owners to ensure all selected values are recognized
  const combinedOwners = useMemo(() => {
    const availableOwners = isMyOwnersOnly ? ownerEntities : value?.items || [];

    const selectedEntities = (selectedOwners ?? []).map(
      ref => cache.getEntity(ref) || parseEntityRef(ref),
    );
    return [...new Set([...selectedEntities, ...availableOwners])];
  }, [isMyOwnersOnly, ownerEntities, value?.items, selectedOwners, cache]);

  // Set selected owners on query parameter updates. This happens at initial page load and from
  // external updates to the page location.
  useEffect(() => {
    if (queryParamOwners?.length) {
      const filter = new EntityOwnerFilter(queryParamOwners);
      setSelectedOwners(filter?.values ?? []);
    }
  }, [queryParamOwners]);

  useEffect(() => {
    // Remove selected owners when user clicks on clear button
    if (clearFilterState.owners) {
      if (!isMyOwnersOnly) {
        updateFilters({
          owners: undefined,
          user: undefined,
        });
      }

      if (userGroups && isMyOwnersOnly) {
        updateFilters({
          owners: undefined,
          user: EntityUserFilter.owned(userGroups),
        });
      }
      setSelectedOwners([]);
    }
  }, [clearFilterState, isMyOwnersOnly, updateFilters, userGroups]);

  useEffect(() => {
    const updateAllFilters = () => {
      updateFilters({
        owners: selectedOwners
          ? new EntityOwnerFilter(selectedOwners)
          : undefined,
        user: EntityUserFilter.all(),
      });
    };

    if (!isMyOwnersOnly) {
      updateAllFilters();
    }
  }, [isMyOwnersOnly, selectedOwners, updateFilters]);

  useEffect(() => {
    const updateSelectedOwnersFilters = () => {
      if (!loadingUserGroups && !userGroups) {
        // For the case where the user identity is not found
        updateFilters({
          user: undefined,
        });
        return;
      }

      if (selectedOwners?.length) {
        updateFilters({
          owners: new EntityOwnerFilter(selectedOwners),
          user: undefined,
        });

        return;
      }

      if (!selectedOwners.length && userGroups) {
        updateFilters({
          owners: undefined,
          user: EntityUserFilter.owned(userGroups),
        });
      }
    };

    if (isMyOwnersOnly) {
      updateSelectedOwnersFilters();
    }
  }, [
    isMyOwnersOnly,
    loadingUserGroups,
    selectedOwners,
    updateFilters,
    userGroups,
  ]);

  useEffect(() => {
    if (isDisabled) {
      setSelectedOwners([]);
    }
  }, [isDisabled]);

  const onChange = (owners: Entity[] | string[]) => {
    setText('');
    resetFilters('owners');

    if (!owners.length) {
      if (!isMyOwnersOnly) {
        updateFilters({
          owners: undefined,
          user: undefined,
        });
      }

      if (userGroups && isMyOwnersOnly) {
        updateFilters({
          owners: undefined,
          user: EntityUserFilter.owned(userGroups),
        });
      }
    }

    setSelectedOwners(
      owners.map(e => {
        const entityRef = typeof e === 'string' ? e : stringifyEntityRef(e);

        if (typeof e !== 'string') {
          cache.setEntity(e);
        }
        return entityRef;
      }),
    );
  };

  return (
    <Box>
      <Autocomplete
        multiple
        limitTags={1}
        disableCloseOnSelect
        loading={loading}
        options={combinedOwners}
        value={selectedOwners as unknown as Entity[]}
        popupIcon={<ExpandMoreIcon />}
        className={!isDisabled ? classes.autocomplete : ''}
        data-testid="entity-owner-picker-autocomplete"
        isOptionEqualToValue={(o: Entity, v: Entity | string) => {
          if (typeof v === 'string') {
            return stringifyEntityRef(o) === v;
          }
          return stringifyEntityRef(o) === stringifyEntityRef(v);
        }}
        getOptionLabel={(o: any) => {
          const entity =
            typeof o === 'string'
              ? cache.getEntity(o) ||
                parseEntityRef(o, {
                  defaultKind: 'group',
                  defaultNamespace: 'default',
                })
              : o;
          return humanizeEntity(entity, humanizeEntityRef(entity));
        }}
        onChange={(_: object, owners) => onChange(owners)}
        filterOptions={x => x}
        renderTags={(elements: any, getTagProps) => {
          return elements.map((item: any, index: number) => {
            const entity =
              typeof item === 'string'
                ? cache.getEntity(item) ||
                  parseEntityRef(item, {
                    defaultKind: 'group',
                    defaultNamespace: 'default',
                  })
                : item;

            return (
              <Chip
                label={entity?.metadata?.name ?? entity?.name}
                size="small"
                {...getTagProps({ index })}
              />
            );
          });
        }}
        renderOption={(
          liProps,
          option,
          state: AutocompleteRenderOptionState,
        ) => (
          <li {...liProps}>
            <RenderOptionLabel entity={option} isSelected={state.selected} />
          </li>
        )}
        renderInput={params => (
          <TextField
            variant="standard"
            label="Owner"
            {...params}
            className={`${classes.common} ${isDisabled ? classes.selectDisabled : classes.select}`}
            disabled={isDisabled}
            InputLabelProps={{ sx: { paddingLeft: '12px' } }}
            InputProps={{
              ...params.InputProps,
              sx: {
                paddingLeft: '8px',
                '&:before': {
                  borderRadius: '2px',
                },
              },
            }}
            onChange={e => {
              setText(e.currentTarget.value);
            }}
            onBlur={() => {
              setText('');
            }}
          />
        )}
        ListboxProps={{
          onScroll: (e: UIEvent<HTMLUListElement>) => {
            const element = e.currentTarget;
            const hasReachedEnd =
              Math.abs(
                element.scrollHeight - element.clientHeight - element.scrollTop,
              ) < 1;

            if (hasReachedEnd && value?.cursor) {
              handleFetch({ items: value.items, cursor: value.cursor });
            }
          },
        }}
        disabled={isDisabled}
      />
    </Box>
  );
};
